import { getCookie } from '../common/util/util.js';

/**
 * Project Board Drag and Drop
 * Handles dragging project cards between status columns
 * and reordering columns via header drag
 */

document.addEventListener('DOMContentLoaded', function() {
    const kanbanBoard = document.getElementById('kanban-board');
    if (!kanbanBoard) return;

    // === Card drag-and-drop ===
    let draggedCard = null;
    let draggedFromColumn = null;

    const cards = document.querySelectorAll('.kanban-card[draggable="true"]');
    const columns = document.querySelectorAll('.kanban-column');

    cards.forEach(card => {
        card.addEventListener('dragstart', handleCardDragStart);
        card.addEventListener('dragend', handleCardDragEnd);
    });

    columns.forEach(column => {
        column.addEventListener('dragover', handleDragOver);
        column.addEventListener('drop', handleDrop);
        column.addEventListener('dragenter', handleDragEnter);
        column.addEventListener('dragleave', handleDragLeave);
    });

    function handleCardDragStart(e) {
        draggedCard = this;
        draggedFromColumn = this.closest('.kanban-column');
        this.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', 'card:' + this.dataset.projectId);
    }

    function handleCardDragEnd() {
        this.classList.remove('dragging');
        draggedCard = null;
        draggedFromColumn = null;
        columns.forEach(col => col.classList.remove('drag-over'));
        document.querySelectorAll('.kanban-drop-zone').forEach(z => z.classList.remove('drop-zone-active'));
    }

    function handleDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }

    function handleDragEnter() {
        if (draggedCard) {
            this.classList.add('drag-over');
        }
    }

    function handleDragLeave(e) {
        if (!this.contains(e.relatedTarget)) {
            this.classList.remove('drag-over');
        }
    }

    function handleDrop(e) {
        e.stopPropagation();

        const targetColumn = this;
        targetColumn.classList.remove('drag-over');

        // Only handle card drops here (column drops handled separately)
        if (!draggedCard || draggedFromColumn === targetColumn) return false;

        const projectId = draggedCard.dataset.projectId;
        const newStatusId = targetColumn.dataset.columnId;
        const cardElement = draggedCard;

        cardElement.style.opacity = '0.6';
        cardElement.style.pointerEvents = 'none';

        fetch('/projects/api/update-project-status/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken'),
            },
            body: JSON.stringify({
                project_id: parseInt(projectId),
                status_id: newStatusId,
            }),
        })
        .then(response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        })
        .then(data => {
            if (data.success) {
                const targetBody = targetColumn.querySelector('.kanban-column-body');
                targetBody.appendChild(cardElement);
                updateColumnCounts();
                updateEmptyStates();
                showNotification('success', data.message || 'Project updated');
            } else {
                showNotification('error', data.error || 'Failed to update project');
            }
            cardElement.style.opacity = '';
            cardElement.style.pointerEvents = '';
        })
        .catch(error => {
            console.error('Error updating project status:', error);
            showNotification('error', 'An error occurred while updating the project');
            cardElement.style.opacity = '';
            cardElement.style.pointerEvents = '';
        });

        return false;
    }

    function updateColumnCounts() {
        columns.forEach(column => {
            const count = column.querySelectorAll('.kanban-column-body .kanban-card').length;
            const countEl = column.querySelector('.kanban-column-count');
            if (countEl) countEl.textContent = count;
        });
    }

    function updateEmptyStates() {
        columns.forEach(column => {
            const body = column.querySelector('.kanban-column-body');
            const cardCount = body.querySelectorAll('.kanban-card').length;
            const emptyState = body.querySelector('.kanban-empty-state');

            if (cardCount === 0 && !emptyState) {
                const div = document.createElement('div');
                div.className = 'kanban-empty-state';
                div.innerHTML = '<p class="text-muted mb-0">No projects</p>';
                body.appendChild(div);
            } else if (cardCount > 0 && emptyState) {
                emptyState.remove();
            }
        });
    }

    function showNotification(type, message) {
        const notification = document.createElement('div');
        notification.className = `alert alert-${type === 'success' ? 'success' : 'danger'} alert-dismissible fade show kanban-notification`;
        notification.innerHTML = `${message} <button type="button" class="close" data-dismiss="alert"><span>&times;</span></button>`;

        const container = document.querySelector('.kanban-container');
        container.insertBefore(notification, container.firstChild);

        setTimeout(() => {
            if (notification.parentNode) notification.remove();
        }, 3000);
    }

    // === Column reordering ===
    let draggedColumn = null;

    columns.forEach(column => {
        const header = column.querySelector('.kanban-column-header');
        header.draggable = true;
        header.style.cursor = 'grab';

        header.addEventListener('dragstart', (e) => {
            // Prevent card drag from interfering
            if (draggedCard) return;
            draggedColumn = column;
            column.classList.add('column-dragging');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', 'column:' + column.dataset.columnId);
        });

        header.addEventListener('dragend', () => {
            if (draggedColumn) {
                draggedColumn.classList.remove('column-dragging');
                draggedColumn = null;
                columns.forEach(col => col.classList.remove('column-drop-target'));
            }
        });
    });

    // Column drop targets (on column elements themselves)
    columns.forEach(column => {
        column.addEventListener('dragover', (e) => {
            if (!draggedColumn || draggedColumn === column) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';

            // Show drop indicator
            columns.forEach(col => col.classList.remove('column-drop-target'));
            column.classList.add('column-drop-target');
        });

        column.addEventListener('drop', (e) => {
            if (!draggedColumn || draggedColumn === column) return;
            e.preventDefault();
            e.stopPropagation();

            column.classList.remove('column-drop-target');

            // Reorder in DOM
            const board = kanbanBoard;
            const rect = column.getBoundingClientRect();
            const midX = rect.left + rect.width / 2;

            if (e.clientX < midX) {
                board.insertBefore(draggedColumn, column);
            } else {
                board.insertBefore(draggedColumn, column.nextSibling);
            }

            // Save new order
            const newOrder = Array.from(board.querySelectorAll('.kanban-column'))
                .map(col => col.dataset.columnId)
                .filter(id => id !== 'none');

            fetch('/projects/api/board-layout/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({ column_order: newOrder }),
            }).catch(err => console.error('Failed to save column order:', err));

            draggedColumn.classList.remove('column-dragging');
            draggedColumn = null;
            rebuildFloatingHeader();
        });
    });

    // === Hidden column drop zones ===
    const dropZones = document.querySelectorAll('.kanban-drop-zone');

    dropZones.forEach(zone => {
        zone.addEventListener('dragover', (e) => {
            if (!draggedCard) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            zone.classList.add('drop-zone-active');
        });

        zone.addEventListener('dragleave', (e) => {
            if (!zone.contains(e.relatedTarget)) {
                zone.classList.remove('drop-zone-active');
            }
        });

        zone.addEventListener('drop', (e) => {
            e.preventDefault();
            e.stopPropagation();
            zone.classList.remove('drop-zone-active');

            if (!draggedCard) return;

            const projectId = draggedCard.dataset.projectId;
            const newStatusId = zone.dataset.columnId;
            const cardElement = draggedCard;

            cardElement.style.opacity = '0.6';
            cardElement.style.pointerEvents = 'none';

            fetch('/projects/api/update-project-status/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({
                    project_id: parseInt(projectId),
                    status_id: newStatusId,
                }),
            })
            .then(response => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    cardElement.remove();
                    updateColumnCounts();
                    updateEmptyStates();
                    showNotification('success', data.message || 'Project updated');
                } else {
                    showNotification('error', data.error || 'Failed to update project');
                    cardElement.style.opacity = '';
                    cardElement.style.pointerEvents = '';
                }
            })
            .catch(error => {
                console.error('Error updating project status:', error);
                showNotification('error', 'An error occurred while updating the project');
                cardElement.style.opacity = '';
                cardElement.style.pointerEvents = '';
            });
        });
    });

    // === Floating column header bar on scroll ===
    const floatingHeader = document.createElement('div');
    floatingHeader.className = 'kanban-floating-header';
    document.body.appendChild(floatingHeader);

    function rebuildFloatingHeader() {
        floatingHeader.innerHTML = '';
        kanbanBoard.querySelectorAll('.kanban-column').forEach(column => {
            const title = column.querySelector('.kanban-column-title')?.textContent?.trim() || '';
            const color = column.style.getPropertyValue('--col-color')?.trim() || '#6c757d';
            const div = document.createElement('div');
            div.className = 'floating-col';
            div.innerHTML = `<span class="status-dot" style="background-color:${color}"></span>${title}`;
            floatingHeader.appendChild(div);
        });
        updateFloatingPositions();
    }

    function updateFloatingPositions() {
        const cols = kanbanBoard.querySelectorAll('.kanban-column');
        const items = floatingHeader.querySelectorAll('.floating-col');
        cols.forEach((col, i) => {
            if (!items[i]) return;
            const rect = col.getBoundingClientRect();
            items[i].style.position = 'absolute';
            items[i].style.left = rect.left + 'px';
            items[i].style.width = rect.width + 'px';
        });
    }

    rebuildFloatingHeader();

    const firstHeader = kanbanBoard.querySelector('.kanban-column-header');
    if (firstHeader) {
        const mainContent = document.querySelector('.main-content');
        const scrollTarget = mainContent || window;

        function onScroll() {
            const rect = firstHeader.getBoundingClientRect();
            const show = rect.bottom < 0;
            floatingHeader.classList.toggle('visible', show);
            if (show) updateFloatingPositions();
        }

        scrollTarget.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', updateFloatingPositions, { passive: true });
    }
});
